import { InputAdornment, TextField } from "@mui/material";
import React, { useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchUserQualifications, fetchUserQualificationsByType } from "../../../services/api/user";
import { useParams } from "react-router-dom";
import BaseTooltip from "../../../components/ui/BaseTooltip";
import { ModuleType, UserType } from "../../../utils/enum";
import useAuth from "../../../hooks/useAuth";
import { verifyOrUnverifyEngineerEquipment } from "../../../services/api/admin";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import AddEquipmentModal from "../../CompleteYourProfile/components/AddEquipmentModal";

const Equipments = ({ equipmentId }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { authRole, getPermissionForModule } = useAuth();
  const { hasWriteAccess: hasEngineerSkillsSetWriteAccess } = getPermissionForModule(
    ModuleType.ENGINEER_SKILLS_SET,
  );
  const { id: userId } = useParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const MAX_ITEMS = 5;

  const {
    data: userQualificationsData,
    isPending: isUserQualificationsApiLoading,
    error: isUserQualificationsApiError,
    refetch: refetchUserQualificationsByType,
  } = useQuery({
    queryKey: ["equipment", userId, equipmentId],
    queryFn: () => fetchUserQualificationsByType(userId, equipmentId),
    enabled: !!userId && !!equipmentId,
  });

  const {
    isPending: isVerifyOrUnverifyEngineerEquipmentMutationApiLoading,
    error: isVerifyOrUnverifyEngineerEquipmentMutationApiError,
    mutateAsync: verifyOrUnverifyEngineerEquipmentMutation,
  } = useMutation({
    mutationFn: verifyOrUnverifyEngineerEquipment,
  });

  const filteredEquipments = userQualificationsData?.data?.filter((equipment) =>
    equipment?.qualificationSubType?.toLowerCase()?.includes(searchQuery?.toLowerCase()),
  );

  const handleVerifyOrUnverifyEquipment = async (payload) => {
    try {
      const updatedEquipment = await verifyOrUnverifyEngineerEquipmentMutation(payload);
      queryClient.setQueryData(["equipment", userId, equipmentId], (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          data: oldData.data.map((item) =>
            item.id === payload.userQualificationId
              ? { ...item, isVerified: payload.isVerified }
              : item,
          ),
        };
      });
    } catch (error) {
      dispatch(
        showToast({
          message: error.response.data.message,
          type: "error",
        }),
      );
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <BaseCard className="py-2 px-8 sm:px-10 rounded-lg" elevation={1}>
      <div className="bg-light -mx-4 px-4 py-2 mt-2 mb-4 rounded-md flex justify-between items-center">
        <h6 className="text-dark font-normal">Equipments</h6>
        {authRole === UserType.ADMIN && (
          <i
            className="fa-light fa-pencil text-gray-900 cursor-pointer"
            style={{ cursor: "pointer" }}
            onClick={() => setIsModalOpen(true)}
          ></i>
        )}
      </div>

      {/* Search Input */}
      <TextField
        placeholder="Search Equipments"
        className="bg-light rounded-md py-1.5 md:w-64"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <i className="fa-solid fa-search text-[#B0B0B0]"></i>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "transparent" },
            "&:hover fieldset": { borderColor: "transparent" },
            "&.Mui-focused fieldset": { borderColor: "transparent" },
          },
        }}
      />

      {/* Equipment List */}
      <div className={`flex flex-wrap gap-4 mt-5 overflow-hidden ${expanded ? "h-auto" : ""}`}>
        {(expanded ? filteredEquipments : filteredEquipments?.slice(0, MAX_ITEMS))?.map(
          (equipment, index) => (
            <BaseTooltip
              key={equipment?.id}
              placement="top"
              interactive={true}
              content={
                equipment?.url ? (
                  <img
                    src={equipment.url}
                    alt="Qualification"
                    className="w-64 max-w-xs h-auto rounded shadow-lg border border-gray-300"
                  />
                ) : (
                  "No preview available"
                )
              }
            >
              <div className="flex items-center px-4 py-1 bg-light rounded-sm cursor-pointer">
                <a href={equipment.url} target="_blank" rel="noopener noreferrer">
                  <p key={equipment?.id}>{equipment?.qualificationSubType}</p>
                </a>
                {UserType.ADMIN === authRole ||
                  (UserType.CLIENT === authRole && (
                    <i
                      className={`${!equipment?.isVerified && "fa-light"} fa-circle  text-sm cursor-pointer ml-2 ${equipment?.isVerified && "fa-solid fa-circle-check text-primary"}`}
                      onClick={() => {
                        if (
                          (authRole === UserType.ADMIN || hasEngineerSkillsSetWriteAccess) &&
                          !isVerifyOrUnverifyEngineerEquipmentMutationApiLoading
                        ) {
                          handleVerifyOrUnverifyEquipment({
                            userQualificationId: equipment.id,
                            isVerified: !equipment?.isVerified,
                          });
                        }
                      }}
                    ></i>
                  ))}
                {/*   <i
                          className="fa-solid fa-xmark ml-2 cursor-pointer hover:text-blue-500"
                          onClick={() => handleDeleteUserQualification(qualification.id)}
                        ></i> */}
              </div>
            </BaseTooltip>
          ),
        )}
      </div>

      {/* View More / View Less Button */}
      {filteredEquipments?.length > MAX_ITEMS && (
        <p
          className="text-primary text-sm cursor-pointer mt-2"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "View Less" : "View More"}
        </p>
      )}

      {/* No Equipment Found */}
      {filteredEquipments?.length === 0 && <p className="text-gray-500 mt-2">No equipment found</p>}

      {isModalOpen && (
        <AddEquipmentModal
          open={isModalOpen}
          close={setIsModalOpen}
          equipmentId={equipmentId}
          refetchUserQualificationsByType={refetchUserQualificationsByType}
          equipments={userQualificationsData?.data}
          targetUserId={userId}
        />
      )}
    </BaseCard>
  );
};

export default Equipments;
